'use client';

/**
 * useBookingState — single source of truth shared tra BookingSidebar (desktop)
 * e MobileBookingFlow (mobile). Sostituisce ~700 righe duplicate.
 *
 * Gestisce:
 * - state primario: guests, extras, time, dayParts, perDayMode, modali
 * - fetch async: slot availability, daily overrides
 * - derived/memo: daysList, rentalPeriods, priceBreakdown, customBreakdown,
 *   customExtrasTotal, effective totals, slotWarning, time options
 * - handlers: toggleExtra, handleTimeChangeSafe
 *
 * I componenti consumer rimangono thin — solo presentazione.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Boat } from '@/types';
import { getSlotAvailability, type SlotAvailability } from '@/services/bookingService';
import { boatDatePriceService } from '@/services/boatDatePriceService';
import {
  calculateRentalPeriods,
  calculatePriceBreakdown,
  dayPartPrice,
  type RentalHoursSettings,
  type ExtraPricing,
  type SeasonalPriceEntry,
} from '@/lib/booking-utils';

export interface ExternalServiceLine {
  id: string;
  name: string;
  total: number;
}

export interface UseBookingStateInput {
  boat: Boat;
  selectedStart: Date | null;
  selectedEnd: Date | null;
  propStartTime?: string;
  propEndTime?: string;
  initialGuests?: number;
  initialExtras?: string[];
  initialDayParts?: Record<string, 'full' | 'morning' | 'afternoon'>;
  rentalHours?: RentalHoursSettings;
  externalServices?: ExternalServiceLine[];
  onTimeChange?: (startTime: string, endTime: string) => void;
}

const DEFAULT_RENTAL_HOURS: RentalHoursSettings = {
  opening: '09:00', closing: '18:00', halfDayCutoff: '13:00',
};

export const dateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function useBookingState(input: UseBookingStateInput) {
  const {
    boat,
    selectedStart,
    selectedEnd,
    propStartTime,
    propEndTime,
    initialGuests = 2,
    initialExtras,
    initialDayParts,
    rentalHours = DEFAULT_RENTAL_HOURS,
    externalServices = [],
    onTimeChange,
  } = input;

  // ─── Primary state ─────────────────────────────────
  const [guests, setGuests] = useState(initialGuests);

  // Mandatory extras: sempre inclusi. Pre-selezionati e non deselezionabili.
  const mandatoryExtraIds = useMemo(
    () => (boat.extras || []).filter(e => e.mandatory).map(e => e.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boat.extras],
  );

  const [selectedExtras, setSelectedExtras] = useState<string[]>(() => {
    const base = initialExtras || [];
    const mandatory = (boat.extras || []).filter(e => e.mandatory).map(e => e.id);
    const merged = [...base];
    for (const id of mandatory) {
      if (!merged.includes(id)) merged.push(id);
    }
    return merged;
  });
  const [startTime, setStartTime] = useState(propStartTime || rentalHours.opening);
  const [endTime, setEndTime] = useState(propEndTime || rentalHours.closing);
  const [slotAvailability, setSlotAvailability] = useState<Record<string, SlotAvailability>>({});
  const [perDayMode, setPerDayMode] = useState(!!(initialDayParts && Object.keys(initialDayParts).length > 0));
  const [dayParts, setDayParts] = useState<Record<string, 'full' | 'morning' | 'afternoon'>>(initialDayParts || {});
  const [dailyOverrides, setDailyOverrides] = useState<Record<string, number>>({});
  // Override manuale mezza giornata per data (markupato dal server). Priorità
  // massima sulla mezza, in parità col booking server.
  const [halfOverrides, setHalfOverrides] = useState<Record<string, number>>({});
  const [showHalfDayBlocked, setShowHalfDayBlocked] = useState(false);
  const [showPerDayModal, setShowPerDayModal] = useState(false);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);

  // ─── Constants from rental hours ───────────────────
  const openingHour = parseInt(rentalHours.opening.split(':')[0]);
  const closingHour = parseInt(rentalHours.closing.split(':')[0]);
  const cutoffHour = parseInt(rentalHours.halfDayCutoff.split(':')[0]);
  const cutoffStr = rentalHours.halfDayCutoff || '13:00';
  const afternoonStart = `${String(cutoffHour + 1).padStart(2, '0')}:00`;
  const halfDayAvailable = (boat.price_half_day || 0) > 0;

  const allTimeOptions = useMemo(
    () => Array.from({ length: closingHour - openingHour + 1 },
      (_, i) => `${(openingHour + i).toString().padStart(2, '0')}:00`),
    [openingHour, closingHour],
  );

  // ─── Sync time props from parent ───────────────────
  useEffect(() => {
    if (propStartTime) setStartTime(propStartTime);
    if (propEndTime) setEndTime(propEndTime);
  }, [propStartTime, propEndTime]);

  // ─── Fetch slot availability on date change ───────
  useEffect(() => {
    if (!selectedStart || !boat.id) return;
    let cancelled = false;
    (async () => {
      try {
        const startStr = dateKey(selectedStart);
        const endStr = selectedEnd ? dateKey(selectedEnd) : startStr;
        const res = await getSlotAvailability(boat.id, startStr, endStr, rentalHours.halfDayCutoff);
        if (!cancelled) setSlotAvailability(res.availability || {});
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('slot availability fetch failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedStart, selectedEnd, boat.id, rentalHours.halfDayCutoff]);

  // ─── Time options + warning derivati da slot ──────
  const { startTimeOptions, endTimeOptions, slotWarning } = useMemo(() => {
    if (!selectedStart || !selectedEnd) {
      return { startTimeOptions: allTimeOptions, endTimeOptions: allTimeOptions, slotWarning: null as string | null };
    }
    const startStr = dateKey(selectedStart);
    const endStr = dateKey(selectedEnd);
    if (startStr !== endStr) {
      return { startTimeOptions: allTimeOptions, endTimeOptions: allTimeOptions, slotWarning: null };
    }
    const slots = slotAvailability[startStr];
    if (!slots) {
      return { startTimeOptions: allTimeOptions, endTimeOptions: allTimeOptions, slotWarning: null };
    }
    if (!slots.morning && slots.afternoon) {
      const minStartHour = slots.afternoonStart ? parseInt(slots.afternoonStart.split(':')[0]) : cutoffHour;
      const afternoonOptions = allTimeOptions.filter(t => parseInt(t.split(':')[0]) >= minStartHour);
      return {
        startTimeOptions: afternoonOptions,
        endTimeOptions: afternoonOptions,
        slotWarning: `Mattina già prenotata - disponibile dalle ${slots.afternoonStart || `${cutoffHour}:00`}`,
      };
    }
    if (slots.morning && !slots.afternoon) {
      const maxEndHour = slots.morningEnd ? parseInt(slots.morningEnd.split(':')[0]) : cutoffHour;
      const morningOptions = allTimeOptions.filter(t => parseInt(t.split(':')[0]) <= maxEndHour);
      return {
        startTimeOptions: morningOptions,
        endTimeOptions: morningOptions,
        slotWarning: `Pomeriggio già prenotato - disponibile fino alle ${slots.morningEnd || `${cutoffHour}:00`}`,
      };
    }
    return { startTimeOptions: allTimeOptions, endTimeOptions: allTimeOptions, slotWarning: null };
  }, [selectedStart, selectedEnd, slotAvailability, allTimeOptions, cutoffHour]);

  // ─── Auto-adjust times when slot options collapse ─
  useEffect(() => {
    if (startTimeOptions.length > 0 && !startTimeOptions.includes(startTime)) {
      const newStartTime = startTimeOptions[0];
      setStartTime(newStartTime);
      onTimeChange?.(newStartTime, endTime);
    }
    if (endTimeOptions.length > 0 && !endTimeOptions.includes(endTime)) {
      const newEndTime = endTimeOptions[endTimeOptions.length - 1];
      setEndTime(newEndTime);
      onTimeChange?.(startTime, newEndTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTimeOptions, endTimeOptions]);

  // ─── Days list ────────────────────────────────────
  const daysList = useMemo<Date[]>(() => {
    if (!selectedStart || !selectedEnd) return [];
    const out: Date[] = [];
    const cur = new Date(selectedStart); cur.setHours(0, 0, 0, 0);
    const end = new Date(selectedEnd); end.setHours(0, 0, 0, 0);
    while (cur.getTime() <= end.getTime()) {
      out.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [selectedStart, selectedEnd]);

  // ─── Maintain dayParts in sync with range ──────────
  useEffect(() => {
    if (daysList.length === 0) return;
    setDayParts(prev => {
      const next: Record<string, 'full' | 'morning' | 'afternoon'> = {};
      const valid = new Set<string>();
      for (const d of daysList) {
        const k = dateKey(d);
        valid.add(k);
        next[k] = prev[k] ?? 'full';
      }
      const same = Object.keys(prev).length === Object.keys(next).length
        && Object.keys(next).every(k => prev[k] === next[k]);
      return same ? prev : next;
    });
  }, [daysList]);

  // ─── Daily overrides fetch ────────────────────────
  useEffect(() => {
    if (!boat.id || !selectedStart || !selectedEnd) {
      setDailyOverrides({});
      setHalfOverrides({});
      return;
    }
    let cancelled = false;
    boatDatePriceService.getForBoat(boat.id, dateKey(selectedStart), dateKey(selectedEnd), { withMarkup: true })
      .then(res => {
        if (cancelled) return;
        type DPRow = { date: string; price: number | null; half_override?: number | null };
        const list = Array.isArray(res.data)
          ? res.data
          : (res.data as { datePrices?: DPRow[] })?.datePrices || [];
        const map: Record<string, number> = {};
        const halfMap: Record<string, number> = {};
        // `dp.price` è già risolto lato server (GET /date-prices): override manuale o
        // regola auto-apply se presenti, altrimenti stagione live — un'unica fonte di
        // verità, niente da ri-derivare qui (evita di duplicare/disallineare la logica
        // di "cos'è autorevole" client vs server, es. price_source='rule_auto').
        for (const dp of list as DPRow[]) {
          if (dp.price != null && dp.price > 0) map[dp.date] = Number(dp.price);
          if (dp.half_override != null && dp.half_override > 0) halfMap[dp.date] = Number(dp.half_override);
        }
        setDailyOverrides(map);
        setHalfOverrides(halfMap);
      })
      .catch(() => { if (!cancelled) { setDailyOverrides({}); setHalfOverrides({}); } });
    return () => { cancelled = true; };
  }, [boat.id, selectedStart, selectedEnd]);

  // ─── Pricing inputs ───────────────────────────────
  const seasonalPrices: SeasonalPriceEntry[] = useMemo(() =>
    (boat.seasonalPrices || []).map(s => ({
      start_date: s.start_date, end_date: s.end_date,
      // MM-GG ricorrenti: sorgente di match per seasonalForDate. Senza questi il
      // resolver non trovava mai la stagione → prezzi fallback alla base barca.
      start_md: (s as { start_md?: string | null }).start_md,
      end_md: (s as { end_md?: string | null }).end_md,
      ranges: (s as { ranges?: unknown }).ranges,
      price_per_day: s.price_per_day, price_half_day: s.price_half_day,
      // Fasce (già markup-ate dal boat-detail-adapter): il motore span le legge sui DUE
      // lati (client+server) → mattina≠pomeriggio distinte, niente drift ±1€.
      slots: (s as { slots?: unknown }).slots,
      // Multi-giorno (già marcati dall'API detail): il tier scavalca il per-day
      // sui DUE lati (calculatePriceBreakdown client + validateBookingPrices server).
      multiday_tiers: (s as { multiday_tiers?: unknown }).multiday_tiers,
      name: s.name, priority: s.priority, is_active: s.is_active,
    })), [boat.seasonalPrices]);

  const boatExtras: ExtraPricing[] = useMemo(() =>
    (boat.extras || []).map(e => ({ id: e.id, price: Number(e.price) || 0, priceType: e.priceType || 'per_stay' })),
    [boat.extras]);

  const rentalPeriods = useMemo(
    () => calculateRentalPeriods(selectedStart, selectedEnd, startTime, endTime, rentalHours),
    [selectedStart, selectedEnd, startTime, endTime, rentalHours]);

  const priceBreakdown = useMemo(() =>
    calculatePriceBreakdown(
      selectedStart, selectedEnd, startTime, endTime,
      boat.price, boat.price_half_day, boatExtras, selectedExtras, guests, rentalHours,
      0.3, seasonalPrices,
      boat.shortStaySurcharge || 0, boat.rentalMode || 'daily',
      dailyOverrides,
      (boat as { duration_rules?: import('@/lib/duration-pricing').DurationRule[] }).duration_rules ?? [],
      halfOverrides,
    ),
    [selectedStart, selectedEnd, startTime, endTime, boat.price, boat.price_half_day,
      boatExtras, selectedExtras, guests, rentalHours, seasonalPrices,
      boat.shortStaySurcharge, boat.rentalMode, dailyOverrides, halfOverrides, boat]);

  const { fullDayTotal, halfDayTotal, halfDayRate, baseTotal, shortStaySurchargeAmount,
    extrasTotal, grandTotal, seasonalBreakdown } = priceBreakdown;
  const hasSeasonal = !!(seasonalBreakdown && seasonalBreakdown.some(d => d.seasonName !== null));

  // Prezzi per fascia del giorno singolo (giornata/mattina/pomeriggio), DISTINTI dal
  // motore span (mattina≠pomeriggio). Per mostrarli sui chip del BoatWizard.
  const fasciaPrices = useMemo(() => {
    if (!selectedStart || !selectedEnd) return null;
    if (new Date(selectedStart).getTime() !== new Date(selectedEnd).getTime()) return null;
    const durRules = (boat as { duration_rules?: import('@/lib/duration-pricing').DurationRule[] }).duration_rules ?? [];
    const at = (cit: string, cot: string) => calculatePriceBreakdown(
      selectedStart, selectedEnd, cit, cot, boat.price, boat.price_half_day,
      [], [], guests, rentalHours, 0.3, seasonalPrices, boat.shortStaySurcharge || 0,
      boat.rentalMode || 'daily', dailyOverrides, durRules, halfOverrides,
    ).baseTotal;
    return { full: at('09:00', '18:00'), morning: at('09:00', cutoffStr), afternoon: at(afternoonStart, '18:00') };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStart, selectedEnd, boat, guests, rentalHours, seasonalPrices, dailyOverrides, halfOverrides, cutoffStr, afternoonStart]);

  // ─── perDayMode breakdown ─────────────────────────
  const customBreakdown = useMemo(() => {
    if (!perDayMode || daysList.length === 0) return null;
    const halfBase = Number(boat.price_half_day) || 0;
    const fullBase = Number(boat.price) || 0;
    const items: { date: string; part: 'full' | 'morning' | 'afternoon'; price: number }[] = [];
    let fullDays = 0, halfDays = 0, base = 0;
    for (const d of daysList) {
      const k = dateKey(d);
      const part = dayParts[k] ?? 'full';
      const isHalf = part !== 'full';
      // Seasonal-aware (stessa sorgente del calendario): stagione per full e half.
      // Passa il LATO reale (morning/afternoon) → pomeriggio prezzato pomeriggio (F2a).
      const price = dayPartPrice(k, part, { dailyOverrides, halfOverrides, seasonalRows: seasonalPrices, fullBase, halfBase });
      if (isHalf) halfDays++; else fullDays++;
      base += price;
      items.push({ date: k, part, price });
    }
    return { items, base, fullDays, halfDays };
  }, [perDayMode, daysList, dayParts, dailyOverrides, halfOverrides, seasonalPrices, boat.price, boat.price_half_day]);

  const customExtrasTotal = useMemo(() => {
    if (!perDayMode || !customBreakdown) return 0;
    const totalUnits = customBreakdown.fullDays + customBreakdown.halfDays;
    let total = 0;
    for (const id of selectedExtras) {
      const ex = boatExtras.find(e => e.id === id);
      if (!ex) continue;
      const p = Number(ex.price) || 0;
      if (ex.priceType === 'per_day') total += p * totalUnits;
      else if (ex.priceType === 'per_person') total += p * guests;
      else total += p;
    }
    return total;
  }, [perDayMode, customBreakdown, selectedExtras, boatExtras, guests]);

  // ─── Effective values (perDayMode-aware) ──────────
  const externalServicesTotal = externalServices.reduce((acc, s) => acc + s.total, 0);
  const effectiveBaseTotal = perDayMode && customBreakdown ? customBreakdown.base : baseTotal;
  const effectiveExtrasTotal = perDayMode ? customExtrasTotal : extrasTotal;
  const effectiveGrandTotal = perDayMode && customBreakdown
    ? customBreakdown.base + customExtrasTotal
    : grandTotal;
  const effectiveFullDays = perDayMode && customBreakdown ? customBreakdown.fullDays : rentalPeriods.fullDays;
  const effectiveHalfDays = perDayMode && customBreakdown ? customBreakdown.halfDays : rentalPeriods.halfDays;
  const hasDates = effectiveFullDays > 0 || effectiveHalfDays > 0;

  // ─── Handlers ─────────────────────────────────────
  const toggleExtra = useCallback((extraId: string) => {
    // Extra obbligatori non possono essere deselezionati.
    if (mandatoryExtraIds.includes(extraId)) return;
    setSelectedExtras(prev =>
      prev.includes(extraId) ? prev.filter(id => id !== extraId) : [...prev, extraId],
    );
  }, [mandatoryExtraIds]);

  /**
   * Time change con check half-day disponibility. Se boat non ha
   * price_half_day configurato, blocca selezione che produce mezza giornata.
   */
  const handleTimeChangeSafe = useCallback((type: 'start' | 'end', value: string): boolean => {
    if (!halfDayAvailable && selectedStart && selectedEnd) {
      const newStart = type === 'start' ? value : startTime;
      const newEnd = type === 'end' ? value : endTime;
      const periods = calculateRentalPeriods(selectedStart, selectedEnd, newStart, newEnd, rentalHours);
      if (periods.halfDays > 0) {
        setShowHalfDayBlocked(true);
        return false;
      }
    }
    if (type === 'start') {
      setStartTime(value);
      onTimeChange?.(value, endTime);
    } else {
      setEndTime(value);
      onTimeChange?.(startTime, value);
    }
    return true;
  }, [halfDayAvailable, selectedStart, selectedEnd, startTime, endTime, rentalHours, onTimeChange]);

  return {
    // Primary state
    guests, setGuests,
    selectedExtras, setSelectedExtras, toggleExtra,
    mandatoryExtraIds,
    startTime, setStartTime, endTime, setEndTime,
    slotAvailability,
    perDayMode, setPerDayMode,
    dayParts, setDayParts,
    dailyOverrides,
    halfOverrides,
    showHalfDayBlocked, setShowHalfDayBlocked,
    showPerDayModal, setShowPerDayModal,
    showBreakdownModal, setShowBreakdownModal,

    // Constants
    openingHour, closingHour, cutoffHour, cutoffStr, afternoonStart,
    halfDayAvailable,
    fasciaPrices,
    allTimeOptions,
    startTimeOptions, endTimeOptions, slotWarning,

    // Derived
    daysList,
    rentalPeriods,
    seasonalPrices,
    boatExtras,
    priceBreakdown,
    customBreakdown,
    customExtrasTotal,

    // Effective totals (perDayMode-aware)
    fullDayTotal, halfDayTotal, halfDayRate, baseTotal, shortStaySurchargeAmount,
    extrasTotal, grandTotal, seasonalBreakdown, hasSeasonal,
    externalServicesTotal,
    effectiveBaseTotal, effectiveExtrasTotal, effectiveGrandTotal,
    effectiveFullDays, effectiveHalfDays, hasDates,

    // Handlers
    handleTimeChangeSafe,

    // Helpers
    dateKey,
  };
}

export type UseBookingStateReturn = ReturnType<typeof useBookingState>;
